//OrderFlowEMI.jsx
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { logout } from '../../redux/actions/authActions';
import useFetch from '../../hooks/useFetch';
import Loader from '../../components/utils/Loader';
import Tooltip from '../../components/utils/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye } from '@fortawesome/free-solid-svg-icons';

const OrderFlowEMI = () => {
  const authState = useSelector(state => state.authReducer);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [userId, setUserId] = useState(authState.user._id);
  const [userName, setUserName] = useState(authState.user.name);
  const [customerCode, setCustomerCode] = useState(authState.user.think5whyscode);
  const [emis, setEMIs] = useState([]);
  const [productCode, setProductCode] = useState('');

  const [fetchData, { loading }] = useFetch();
  const [selectedEMIId, setSelectedEMIId] = useState(null);

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isLoggedIn = authState?.isLoggedIn || false;

  const handleLogout = () => {
    dispatch(logout());
    navigate("/orderflow-home");
  };

  useEffect(() => {
    fetchEMIs();
  }, [authState]);

  const fetchEMIs = useCallback(() => {
    const config = { 
        url: "/emi/emilist", 
        method: "post", 
        data: {customerCode: customerCode, productCode: productCode},
        headers: { Authorization: authState.token } 
      };
      // console.log("EMI Config: ",config)
  
      fetchData(config, { showSuccessToast: false })
        .then((response) => {
          setEMIs(response?.emis || []);  
        })
        .catch(error => console.error("Error fetching data:", error));
      
  }, [authState.token, fetchData]);
  
  const formatDateTime = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    const hours = jsDate.getHours().toString().padStart(2, '0');
    const minutes = jsDate.getMinutes().toString().padStart(2, '0');
    const seconds = jsDate.getSeconds().toString().padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
};

const formatDate = (date) => {
    const options = {
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit',
        hour12: false
    };
    const jsDate = new Date(date);
    const day = jsDate.getDate().toString().padStart(2, '0');
    const month = (jsDate.getMonth() + 1).toString().padStart(2, '0');
    const year = jsDate.getFullYear();
    return `${day}-${month}-${year}`;
};

  const sortedEMIs = [...emis].sort((a, b) => (b.emiTitle || '').localeCompare(a.emiTitle || ''));

  return (
    <div className="flex flex-col min-h-screen bg-gray-900 text-gray-100 [&_p]:text-gray-100">
      <header className="sticky top-0 z-50 bg-black border border-cyan-400 px-4 py-2 flex flex-row items-center justify-between shadow-lg">
        <div className='flex flex-col items-center sm:items-start justify-start w-full sm:w-auto'>
          <h2 className='text-lg sm:text-xl lg:text-2xl font-bold text-yellow-200 text-center mb-1 sm:mb-2'>
            <i className="fas fa-shopping-cart text-lg sm:text-xl lg:text-2xl text-yellow-200 mr-2"></i>Khareedo
          </h2>
          <h2 className='text-xs sm:text-sm font-bold text-white text-center'>
            My EMIs
          </h2> 
        </div>  
        <div className="flex items-center">
          <div className="hidden md:flex items-center gap-3">
            {isLoggedIn ? (
              <>
                <div className="text-xs text-left text-blue-300">{userName}</div>
                <button
                  onClick={handleLogout}
                  className="p-2 mt-2 rounded-md bg-blue-600 hover:bg-blue-700 transition"
                  title="Logout"
                >
                  ⏻
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-3 py-1 rounded-full border border-blue-700 text-sm text-blue-200 hover:bg-blue-900"
                >
                  Login
                </Link>
                <Link
                  to="/signup"
                  className="px-3 py-1 rounded-full bg-blue-500 text-white text-sm hover:bg-blue-400"
                >
                  Register
                </Link>
              </>
            )}
          </div>
        
          {/* Mobile hamburger */}
          <button
            onClick={() => setIsMobileMenuOpen((s) => !s)}
            className="md:hidden w-6 h-6 flex items-center justify-center text-xl text-blue-300 bg-blue-500 rounded"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? "×" : "☰"}
          </button>
        </div>
      </header>
      
      {/* Mobile menu content */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-black border-b border-cyan-400 px-4 py-2 animate-fadeIn">
          {isLoggedIn ? (
            <div className="flex flex-row items-center justify-center gap-3">
              <span className="text-blue-300 font-medium whitespace-nowrap h-7">
                Welcome, {userName}
              </span>
              <button
                onClick={handleLogout}
                className="rounded-md bg-blue-600 hover:bg-blue-700 w-7 h-7 flex items-center justify-center text-white"
                title="Logout"
              >
                ⏻
              </button>
            </div>
          ) : (
            <div className="flex flex-row items-center justify-center gap-6">
              <Link to="/login" className="text-blue-300 hover:text-yellow-300">
                Login
              </Link>
              <span className="text-blue-500">|</span>
              <Link to="/signup" className="text-blue-300 hover:text-yellow-300">
                Register
              </Link>
            </div>
          )}
        </div>
      )}  

      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="border border-blue-500 p-4 sm:p-6 bg-gray-800 rounded-lg shadow-lg">
            <p className="flex justify-center text-lg sm:text-xl font-semibold mt-1 mx-2 mb-2 text-yellow-200">EMIs</p>
            {emis?.length > 0 && (
              <h3 className="text-sm sm:text-base mx-1 mb-2 text-yellow-200 text-center sm:text-left">
                {emis.length} {emis.length === 1 ? "EMI Found" : "EMIs Found"}!
              </h3>
            )}

            {loading ? (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            ) : emis?.length === 0 ? (
              <div className="min-h-[200px] sm:min-h-[300px] flex items-center justify-center gap-4 text-yellow-300">
                <h3 className="text-base sm:text-lg text-orange-400">No Record Found!</h3>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {sortedEMIs.map((emi, index) => (
                  <div
                    key={emi._id}
                    className="bg-black p-3 sm:p-4 rounded-md shadow-md transition-all hover:bg-gray-600 border border-gray-600"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-0">
                      <div className="text-white text-sm sm:text-base">
                        <span className="font-bold text-yellow-300">{index + 1}.</span> 
                        <span className="ml-1">{emi.emiTitle}</span>
                      </div>

                      <Tooltip text="View Details" position="top">
                        <button
                          className="text-blue-500 hover:text-blue-300 w-10 self-end sm:self-auto p-1 rounded transition-colors"
                          onClick={() => setSelectedEMIId(selectedEMIId === emi._id ? null : emi._id)}
                        >
                          <FontAwesomeIcon icon={faEye} className="text-sm sm:text-base" />
                        </button>
                      </Tooltip>
                    </div>

                    {selectedEMIId === emi._id && (
                      <div className="bg-gray-800 text-white mt-3 sm:mt-4 p-3 sm:p-4 rounded-lg space-y-2 text-xs sm:text-sm">
                        <p className="text-yellow-200 text-base sm:text-lg font-semibold">{emi.emiTitle}</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-left">
                          <p><strong>Product:</strong> {emi.productName}</p>
                          <p><strong>EMI Number:</strong> {emi.emiNumber}</p>
                          <p><strong>Amount:</strong> {emi.emiAmount}</p>
                          <p><strong>Payment Details:</strong> {emi.emiPaymentDetails}</p>
                          <p><strong>Remarks:</strong> {emi.emiRemarks}</p>
                          <p><strong>EMI Due Date:</strong> {formatDate(emi.emiDueDate)}</p>
                          <p><strong>EMI Date:</strong> {formatDate(emi.emiDate)}</p>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#2a0f2f] border-t border-blue-800 text-blue-300 py-4">
        <div className="max-w-6xl mx-auto px-3 flex flex-col md:flex-row items-center justify-between gap-3">
          <div className="text-xs lg:text-sm">
            © 2025 Think5Whys Global DeepTech Solutions. All rights reserved.
          </div>
          <div className="flex flex-wrap gap-4 text-xs lg:text-sm">
            <Link to="/about-us" className="text-white hover:text-blue-400">About</Link>
            <Link to="/privacy-policy" className="text-white hover:text-blue-400">Privacy Policy</Link>
            <Link to="/terms-of-use" className="text-white hover:text-blue-400">Terms of Use</Link>
            <Link to="/contact-us" className="text-white hover:text-blue-400">Contact</Link>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default OrderFlowEMI